import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import { Check } from '@mui/icons-material';
import type { PostItColor, CreatePostItInput } from '@/types/thinkspace';

const POST_IT_COLORS: { color: PostItColor; hex: string; label: string }[] = [
  { color: 'yellow', hex: '#FFF59D', label: 'Yellow' },
  { color: 'pink', hex: '#F8BBD9', label: 'Pink' },
  { color: 'blue', hex: '#90CAF9', label: 'Blue' },
  { color: 'green', hex: '#A5D6A7', label: 'Green' },
  { color: 'orange', hex: '#FFCC80', label: 'Orange' },
  { color: 'purple', hex: '#CE93D8', label: 'Purple' },
];

type CreatePostItWizardProps = {
  open: boolean;
  onClose: () => void;
  onCreate: (input: CreatePostItInput) => void;
  isFirstNote?: boolean;
};

export const CreatePostItWizard = ({
  open,
  onClose,
  onCreate,
  isFirstNote = false,
}: CreatePostItWizardProps) => {
  const [step, setStep] = useState(0);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [color, setColor] = useState<PostItColor>('yellow');

  const steps = ['Choose a color', 'Add your note'];

  const handleCreate = () => {
    onCreate({
      title: title.trim() || 'New Note',
      content: content.trim(),
      color,
    });
    handleClose();
  };

  const handleClose = () => {
    setStep(0);
    setTitle('');
    setContent('');
    setColor('yellow');
    onClose();
  };

  const handleNext = () => {
    if (step < steps.length - 1) {
      setStep(step + 1);
    } else {
      handleCreate();
    }
  };

  const handleBack = () => {
    setStep(step - 1);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isFirstNote ? 'Create Your First Sticky Note' : 'Add Sticky Note'}
      </DialogTitle>

      <DialogContent>
        {isFirstNote && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Let's get started! Create your first sticky note by choosing a color and adding some content.
          </Typography>
        )}

        <Stepper activeStep={step} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {step === 0 && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Pick a color for your note
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center', mt: 2 }}>
              {POST_IT_COLORS.map((c) => (
                <Box
                  key={c.color}
                  onClick={() => setColor(c.color)}
                  sx={{
                    width: 80,
                    height: 80,
                    backgroundColor: c.hex,
                    borderRadius: 2,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: c.color === color ? '4px solid #0f2ccf' : '2px solid rgba(0,0,0,0.1)',
                    transition: 'transform 0.2s, border 0.2s',
                    '&:hover': {
                      transform: 'scale(1.05)',
                    },
                  }}
                >
                  {c.color === color && <Check sx={{ fontSize: 32, color: 'rgba(0,0,0,0.6)' }} />}
                </Box>
              ))}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 2 }}>
              Selected: {POST_IT_COLORS.find((c) => c.color === color)?.label}
            </Typography>
          </Box>
        )}

        {step === 1 && (
          <Box>
            <TextField
              autoFocus
              label="Title"
              fullWidth
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              sx={{ mb: 2 }}
              placeholder="Give your note a title"
            />
            <TextField
              label="Content"
              fullWidth
              multiline
              rows={4}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="What's on your mind?"
            />

            {/* Preview */}
            <Box sx={{ mt: 3 }}>
              <Typography variant="caption" color="text.secondary">
                Preview
              </Typography>
              <Box
                sx={{
                  mt: 1,
                  p: 2,
                  backgroundColor: POST_IT_COLORS.find((c) => c.color === color)?.hex,
                  borderRadius: 1,
                  boxShadow: '2px 4px 8px rgba(0,0,0,0.15)',
                  maxWidth: 200,
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {title || 'Untitled'}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {content || 'Your content here...'}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose}>Cancel</Button>
        {step > 0 && <Button onClick={handleBack}>Back</Button>}
        <Button variant="contained" onClick={handleNext}>
          {step === steps.length - 1 ? 'Create' : 'Next'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
